import Cookies from 'js-cookie';

import {
  ATTRIBUTION_COOKIE_DOMAIN,
  writeAttributionCookie
} from '@lib/attribution/cookies';

const REWARDFUL_KEY = 'rewardful_referral_id';

// FFH-1991: our own persistent first-party referral cookie. Mirrors the UTM
// first-party cookie model (apex domain, 30-day, via the shared attribution
// writer) so an affiliate referral survives a click-now-pay-later conversion
// in a later session — independent of Rewardful's rw.js having re-loaded on
// the return visit (async + consent-gated, so it can lag behind checkout on a
// direct-to-checkout return hit).
const REFERRAL_COOKIE = 'ffh_rewardful_referral';

/**
 * FFH-1991: register the referral capture on Rewardful's own `ready` queue.
 *
 * rw.js is consent-gated and async, so any fixed timer can fire before the
 * SDK exists and silently miss the referral (the root cause of affiliates
 * losing cross-session attribution). The queue stub below is Rewardful's own
 * embed snippet: a pure JS queue with no tracking of its own, safe to install
 * unconditionally. Whenever rw.js loads — immediately, or minutes later once
 * consent is granted — it drains the queue and fires `ready`, at which point
 * `window.Rewardful.referral` is guaranteed populated and we persist it.
 */
export const registerReferralCapture = (): void => {
  if (typeof window === 'undefined') return;

  // Rewardful's official queue stub, mirrored VERBATIM from the embed snippet
  // in _app.tsx (including pushing `arguments` objects — rw.js drains whatever
  // queue exists at load, and this stub can win the `w[r] = w[r] || …` race
  // against the consent-gated snippet, so its entries must be
  // indistinguishable from the vendor's). Keeps any existing stub.
  window._rwq = 'rewardful';
  window.rewardful =
    window.rewardful ||
    function () {
      // eslint-disable-next-line prefer-rest-params -- vendor stub semantics
      (window.rewardful.q = window.rewardful.q || []).push(arguments);
    };

  window.rewardful('ready', () => {
    try {
      storeReferralId();
    } catch {
      // Storage can throw (private mode, disabled storage); attribution must
      // never break the page.
    }
  });
};

export const storeReferralId = () => {
  if (typeof window === 'undefined') return null;

  const existingReferralId = sessionStorage.getItem(REWARDFUL_KEY);
  if (existingReferralId) {
    return existingReferralId;
  }

  const referralId = window?.Rewardful?.referral || null;

  if (referralId) {
    sessionStorage.setItem(REWARDFUL_KEY, referralId);
    // FFH-1991: also persist to a first-party cookie so it survives across
    // sessions, the same way UTM first/last-touch does.
    writeAttributionCookie(REFERRAL_COOKIE, referralId);
  }

  return referralId;
};

export const getStoredReferralId = () => {
  if (typeof window === 'undefined') return null;

  return sessionStorage.getItem(REWARDFUL_KEY);
};

/**
 * FFH-1991: resolve the referral to send at checkout, in order of freshness:
 *   1. Rewardful's live value (`window.Rewardful.referral`) — reflects its
 *      persistent cookie for the current page load.
 *   2. Our own persistent first-party cookie — survives a return session even
 *      before rw.js has loaded (the click-now-pay-later case, including a
 *      direct hit straight into /checkout-stripe).
 *   3. The same-session sessionStorage snapshot.
 *
 * Fixes affiliate referrals silently dropping on any conversion that happens
 * in a later session than the affiliate click — UTM already persists this way.
 */
export const resolveReferralId = (): string | null => {
  if (typeof window === 'undefined') return null;

  const liveReferral: unknown = window?.Rewardful?.referral;
  if (typeof liveReferral === 'string' && liveReferral) return liveReferral;

  const cookieReferral = Cookies.get(REFERRAL_COOKIE);
  if (cookieReferral) return cookieReferral;

  return getStoredReferralId();
};

export const clearStoredReferralId = () => {
  if (typeof window === 'undefined') return;

  sessionStorage.removeItem(REWARDFUL_KEY);
  // FFH-1991: also clear the persistent cookie so a used referral isn't
  // re-sent on an unrelated later purchase.
  Cookies.remove(REFERRAL_COOKIE, {
    domain: ATTRIBUTION_COOKIE_DOMAIN,
    path: '/'
  });
};
